import React, { useRef, useEffect } from 'react';
import { Card, CardBody, Chip, Tooltip } from '@heroui/react';
import {
  Hash,
  TextAa,
  CalendarBlank,
  ToggleLeft,
  Infinity,
  Question,
  Copy,
} from '@phosphor-icons/react';
import { toast } from 'react-toastify';

interface PropertyCardProps {
  name: string;
  value: any;
  tableName: string;
  type?: string;
  highlighted?: boolean;
  registerMatchRef?: (ref: HTMLDivElement | null, relationName?: string) => void;
}

const PropertyCard: React.FC<PropertyCardProps> = ({
  name,
  value,
  tableName,
  type,
  highlighted = false,
  registerMatchRef,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);

  // Register this card as a match if highlighted
  useEffect(() => {
    if (highlighted && registerMatchRef && cardRef.current) {
      registerMatchRef(cardRef.current);
    }
  }, [highlighted, registerMatchRef]);
  const formatValue = (val: any): string => {
    if (val === null || val === undefined) {
      return 'NULL';
    }

    if (typeof val === 'boolean') {
      return val ? 'true' : 'false';
    }

    if (typeof val === 'object') {
      return JSON.stringify(val);
    }

    if (typeof val === 'string' && val.length > 100) {
      return val.substring(0, 100) + '...';
    }

    return String(val);
  };

  const getTypeIcon = (typeName?: string) => {
    if (!typeName) return <Question size={16} />;

    const lowerType = typeName.toLowerCase();

    if (lowerType.includes('int') || lowerType.includes('number') || lowerType.includes('numeric')) {
      return <Hash size={16} />;
    }

    if (lowerType.includes('text') || lowerType.includes('varchar') || lowerType.includes('char')) {
      return <TextAa size={16} />;
    }

    if (lowerType.includes('date') || lowerType.includes('time')) {
      return <CalendarBlank size={16} />;
    }

    if (lowerType.includes('bool')) {
      return <ToggleLeft size={16} />;
    }

    if (lowerType.includes('uuid')) {
      return <Infinity size={16} />;
    }

    return <Question size={16} />;
  };

  const getTypeColor = (typeName?: string): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' => {
    if (!typeName) return 'default';

    const lowerType = typeName.toLowerCase();

    if (lowerType.includes('int') || lowerType.includes('number')) return 'primary';
    if (lowerType.includes('text') || lowerType.includes('varchar')) return 'secondary';
    if (lowerType.includes('date') || lowerType.includes('time')) return 'success';
    if (lowerType.includes('bool')) return 'warning';
    if (lowerType.includes('uuid')) return 'danger';

    return 'default';
  };

  const isNull = value === null || value === undefined;

  const handleCopyValue = () => {
    // Copy placeholder format: {tableName.propertyName}
    const placeholder = `{${tableName}.${name}}`;
    navigator.clipboard.writeText(placeholder);
    toast.success(`Placeholder gekopieerd: ${placeholder}`, { position: 'bottom-right' });
  };

  return (
    <Card
      ref={cardRef}
      className={`
        ${highlighted ? 'ring-2 ring-purple-500 bg-purple-50/50' : 'bg-white'}
        ${isNull ? 'opacity-60' : ''}
        hover:shadow-md transition-shadow cursor-default
      `}
    >
      <CardBody className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {/* Property Name */}
            <div className="flex items-center gap-2 mb-2">
              <span className="font-mono text-sm font-medium text-gray-700">
                {name}
              </span>
              {type && (
                <Chip
                  size="sm"
                  variant="flat"
                  color={getTypeColor(type)}
                  startContent={getTypeIcon(type)}
                >
                  {type}
                </Chip>
              )}
            </div>

            {/* Property Value */}
            <div className={`
              text-sm font-mono break-all
              ${isNull ? 'text-gray-400 italic' : 'text-gray-900'}
            `}>
              {formatValue(value)}
            </div>
          </div>

          {/* Copy Button - Always show, even for NULL values */}
          <Tooltip content="Kopieer placeholder" placement="top">
            <button
              onClick={handleCopyValue}
              className="
                p-2 rounded-md
                text-gray-400 hover:text-gray-600 hover:bg-gray-100
                transition-colors
                flex-shrink-0
              "
            >
              <Copy size={18} />
            </button>
          </Tooltip>
        </div>
      </CardBody>
    </Card>
  );
};

export default PropertyCard;
